import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccessTimeRounded,
  AddRounded,
  AssignmentTurnedInRounded,
  ContentCopyRounded,
  GroupsRounded,
  PaidOutlined,
  WorkOutlineRounded,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listMatters } from '../features/matters/api';
import {
  createTeamMember,
  createTimeEntry,
  getCurrentUser,
  getPracticeSummary,
  listTeam,
  listTimeEntries,
  updateTimeEntry,
} from '../features/account/api';
import { ACTIVE_MATTER_KEY } from '../edition';
import { firmDisplayName, firmProfile } from '../edition';
import FirmProfileCard from '../components/FirmProfileCard';

const emptyTime = () => ({
  matterId: localStorage.getItem(ACTIVE_MATTER_KEY) || '',
  workDate: new Date().toISOString().slice(0, 10),
  hours: '0.5',
  description: '',
  activity: 'Legal work',
  billable: true,
  status: 'draft',
});

const emptyMember = { name: '', email: '', role: 'paralegal', professionalTitle: 'Paralegal' };

function hours(minutes) {
  return `${(Number(minutes || 0) / 60).toFixed(Number(minutes || 0) % 60 ? 1 : 0)} h`;
}

function money(cents) {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(Number(cents || 0) / 100);
}

function shortDate(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(`${value}T12:00:00`));
}

function Metric({ label, value, note, icon, color }) {
  return (
    <Card sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.4} alignItems="center">
        <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: `${color}14`, color }}>{icon}</Box>
        <Box><Typography sx={{ fontSize: 23, lineHeight: 1.1, fontWeight: 800 }}>{value}</Typography><Typography variant="body2" sx={{ fontWeight: 680 }}>{label}</Typography>{note && <Typography variant="caption" color="text.secondary">{note}</Typography>}</Box>
      </Stack>
    </Card>
  );
}

export default function PracticeAdministration() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [matters, setMatters] = useState([]);
  const [team, setTeam] = useState([]);
  const [identity, setIdentity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [timeOpen, setTimeOpen] = useState(false);
  const [time, setTime] = useState(emptyTime);
  const [memberOpen, setMemberOpen] = useState(false);
  const [member, setMember] = useState(emptyMember);
  const [inviteReady, setInviteReady] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [summaryBody, timeBody, matterBody, teamBody, meBody] = await Promise.all([
        getPracticeSummary(), listTimeEntries(), listMatters(), listTeam(), getCurrentUser(),
      ]);
      setSummary(summaryBody);
      setEntries(timeBody.entries || []);
      setMatters(matterBody.matters || []);
      setTeam(teamBody.team || []);
      setIdentity(meBody);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.resolve().then(refresh);
  }, [refresh]);

  useEffect(() => {
    if (searchParams.get('newTime') !== '1') return;
    Promise.resolve().then(() => {
      setTime(emptyTime());
      setTimeOpen(true);
      setSearchParams({}, { replace: true });
    });
  }, [searchParams, setSearchParams]);

  const canApprove = ['admin', 'lawyer', 'paralegal'].includes(identity?.user?.role);
  const canManageTeam = identity?.authenticated && identity?.user?.role === 'admin';
  const recentEntries = useMemo(() => entries.slice(0, 12), [entries]);

  async function saveTime() {
    const minutes = Math.round(Number(time.hours) * 60);
    if (!Number.isFinite(minutes) || minutes < 1 || !time.description.trim()) return;
    setSaving(true);
    setError('');
    try {
      await createTimeEntry({ ...time, minutes });
      setTimeOpen(false);
      setTime(emptyTime());
      setNotice('Time recorded.');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(entry, status) {
    setSaving(true);
    try {
      await updateTimeEntry(entry.id, { status });
      setNotice(status === 'approved' ? 'Time approved.' : 'Time submitted.');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveMember() {
    setSaving(true);
    setError('');
    try {
      const result = await createTeamMember(member);
      setMemberOpen(false);
      setMember(emptyMember);
      setInviteReady(result.invitation);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading && !summary) return <Box sx={{ height: '60vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', pb: 5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 2.5 }}>
        <Box><Typography variant="overline" color="secondary.main">{identity?.user?.organizationName || firmProfile.legalName} · {identity?.user?.organizationJurisdiction || firmProfile.jurisdiction}</Typography><Typography variant="h3">{firmDisplayName(identity?.user?.organizationName)} practice</Typography><Typography color="text.secondary" sx={{ mt: 0.5 }}>Time, team, standards and approvals in one place—built to handle a high matter volume.</Typography></Box>
        <Button variant="contained" startIcon={<AccessTimeRounded />} onClick={() => { setTime(emptyTime()); setTimeOpen(true); }}>Log time</Button>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 2 }}><FirmProfileCard compact identity={identity?.user} /></Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <Metric label="Active matters" value={summary?.activeMatters || 0} note={`${summary?.upcomingDates || 0} court dates this week`} icon={<WorkOutlineRounded />} color="#245B88" />
        <Metric label="Time this month" value={hours(summary?.monthMinutes)} note={summary?.scope === 'mine' ? 'Your time' : 'Firm total'} icon={<AccessTimeRounded />} color="#0D9488" />
        <Metric label="Unbilled time" value={hours(summary?.unbilledMinutes)} note={summary?.unbilledCents ? money(summary.unbilledCents) : 'Rate optional'} icon={<PaidOutlined />} color="#D97706" />
        <Metric label="Awaiting approval" value={summary?.awaitingApproval || 0} note="Completed workflow tasks" icon={<AssignmentTurnedInRounded />} color="#6D4FB3" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.45fr) minmax(320px, .7fr)' }, gap: 2 }}>
        <Card sx={{ p: { xs: 1.5, md: 2.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}><Box><Typography variant="h5">Recent time</Typography><Typography variant="body2" color="text.secondary">Work stays linked to the matter.</Typography></Box><Button size="small" startIcon={<AddRounded />} onClick={() => setTimeOpen(true)}>Add</Button></Stack>
          {!recentEntries.length ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}><AccessTimeRounded sx={{ color: 'text.disabled', fontSize: 36 }} /><Typography sx={{ fontWeight: 730, mt: 1 }}>No time recorded yet</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Start with the work you just completed.</Typography><Button variant="contained" onClick={() => setTimeOpen(true)}>Log time</Button></Paper>
          ) : (
            <Stack spacing={0}>
              {recentEntries.map(entry => (
                <Stack key={entry.id} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.2} sx={{ py: 1.35, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ width: { sm: 80 } }}><Typography variant="body2" fontWeight={750}>{hours(entry.minutes)}</Typography><Typography variant="caption" color="text.secondary">{shortDate(entry.workDate)}</Typography></Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="body2" fontWeight={680}>{entry.description}</Typography><Typography variant="caption" color="text.secondary">{entry.matterNumber ? `${entry.matterNumber} · ${entry.matterTitle}` : 'General firm work'} · {entry.userName}</Typography></Box>
                  <Stack direction="row" alignItems="center" spacing={0.7}><Chip size="small" label={entry.status} color={entry.status === 'approved' ? 'success' : entry.status === 'submitted' ? 'warning' : 'default'} />{entry.status === 'draft' && <Button size="small" onClick={() => changeStatus(entry, 'submitted')} disabled={saving}>Submit</Button>}{entry.status === 'submitted' && canApprove && <Button size="small" onClick={() => changeStatus(entry, 'approved')} disabled={saving}>Approve</Button>}</Stack>
                </Stack>
              ))}
            </Stack>
          )}
        </Card>

        <Stack spacing={2}>
          <Card sx={{ p: 2.25 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}><Box><Typography variant="h5">Team</Typography><Typography variant="body2" color="text.secondary">{identity?.user?.organizationName || 'Your practice'}</Typography></Box>{canManageTeam && <Button size="small" startIcon={<AddRounded />} onClick={() => setMemberOpen(true)}>Invite</Button>}</Stack>
            {!identity?.authenticated && <Alert severity="info" sx={{ mb: 1.5 }}>Sign in with a NEO Legal invitation to add team members and keep firm matters private.</Alert>}
            <Stack spacing={1}>
              {team.map(person => <Stack key={person.id} direction="row" alignItems="center" spacing={1}><Box sx={{ width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: '#fff', fontWeight: 800 }}>{person.name.split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase()}</Box><Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="body2" fontWeight={720}>{person.name}</Typography><Typography variant="caption" color="text.secondary">{person.email}</Typography></Box><Chip size="small" label={person.professionalTitle || (person.role === 'admin' ? 'firm administrator' : person.role)} variant="outlined" /></Stack>)}
              {!team.length && <Stack direction="row" alignItems="center" spacing={1} sx={{ opacity: 0.7 }}><GroupsRounded /><Typography variant="body2">No team members to show.</Typography></Stack>}
            </Stack>
          </Card>
          <Card sx={{ p: 2.25 }}>
            <Typography variant="h5">NEO operating standards</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>The service commitments applied across intake, matters and closing.</Typography>
            <Stack spacing={1}>
              {firmProfile.serviceStandards.map((standard, index) => <Stack key={standard} direction="row" spacing={1} alignItems="flex-start"><Chip size="small" label={index + 1} color="secondary" sx={{ minWidth: 28 }} /><Typography variant="body2">{standard}</Typography></Stack>)}
            </Stack>
          </Card>
          <Card sx={{ p: 2.25 }}><Typography variant="h5">Keep work moving</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>Approvals remain inside each matter, beside the work being reviewed.</Typography><Button fullWidth variant="outlined" onClick={() => navigate('/matters?view=workflow')}>Open matter workflow</Button></Card>
        </Stack>
      </Box>

      <Dialog open={timeOpen} onClose={() => setTimeOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Log time</DialogTitle>
        <DialogContent dividers><Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField label="Matter" select value={time.matterId} onChange={event => setTime(current => ({ ...current, matterId: event.target.value }))}><MenuItem value="">General firm work</MenuItem>{matters.map(item => <MenuItem key={item.id} value={item.id}>{item.matterNumber} · {item.title}</MenuItem>)}</TextField>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}><TextField label="Date" type="date" InputLabelProps={{ shrink: true }} value={time.workDate} onChange={event => setTime(current => ({ ...current, workDate: event.target.value }))} /><TextField label="Hours" type="number" inputProps={{ min: 0.1, max: 24, step: 0.1 }} value={time.hours} onChange={event => setTime(current => ({ ...current, hours: event.target.value }))} /></Box>
          <TextField label="Work completed" value={time.description} onChange={event => setTime(current => ({ ...current, description: event.target.value }))} multiline minRows={3} required autoFocus />
          <TextField label="Activity" value={time.activity} onChange={event => setTime(current => ({ ...current, activity: event.target.value }))} />
          <FormControlLabel control={<Checkbox checked={time.billable} onChange={event => setTime(current => ({ ...current, billable: event.target.checked }))} />} label="Billable" />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setTimeOpen(false)}>Cancel</Button><Button variant="contained" onClick={saveTime} disabled={saving || !time.description.trim() || Number(time.hours) <= 0}>{saving ? 'Saving…' : 'Save time'}</Button></DialogActions>
      </Dialog>

      <Dialog open={memberOpen} onClose={() => setMemberOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Invite team member</DialogTitle>
        <DialogContent dividers><Stack spacing={2} sx={{ pt: 0.5 }}><Alert severity="info">Each person creates an individual NEO workspace password from a one-time Lex Maple setup link. No temporary password is sent or stored.</Alert><TextField label="Name" value={member.name} onChange={event => setMember(current => ({ ...current, name: event.target.value }))} required /><TextField label="Work email" type="email" value={member.email} onChange={event => setMember(current => ({ ...current, email: event.target.value }))} required /><TextField select label="Role" value={member.role} onChange={event => setMember(current => ({ ...current, role: event.target.value, professionalTitle: event.target.value === 'lawyer' ? 'Lawyer' : 'Paralegal' }))}><MenuItem value="paralegal">Paralegal</MenuItem><MenuItem value="lawyer">Lawyer</MenuItem></TextField></Stack></DialogContent>
        <DialogActions><Button onClick={() => setMemberOpen(false)}>Cancel</Button><Button variant="contained" onClick={saveMember} disabled={saving || !member.name.trim() || !member.email.includes('@')}>{saving ? 'Inviting…' : 'Invite member'}</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(inviteReady)} onClose={() => setInviteReady(null)} fullWidth maxWidth="sm">
        <DialogTitle>Setup link ready</DialogTitle>
        <DialogContent dividers>
          <Alert severity="success" sx={{ mb: 2 }}>The invitation is ready for {inviteReady?.email}. Send this link only to that person; it expires in seven days.</Alert>
          <TextField fullWidth label="One-time NEO workspace setup link" value={inviteReady?.inviteUrl || ''} InputProps={{ readOnly: true }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setInviteReady(null)}>Done</Button><Button variant="contained" startIcon={<ContentCopyRounded />} onClick={async () => { await navigator.clipboard.writeText(inviteReady?.inviteUrl || ''); setNotice('Setup link copied.'); }}>Copy link</Button></DialogActions>
      </Dialog>
      <Snackbar open={Boolean(notice)} autoHideDuration={3500} onClose={() => setNotice('')} message={notice} />
    </Box>
  );
}
